import { retrieveLaunchParams } from '@telegram-apps/sdk-react';
import {
  HttpError,
  NetworkError,
  TimeoutError,
  apiRequest,
  authHeaders,
  isLoginRequiredError,
  isMaintenanceError,
} from '@/shared/api';
import { isTelegram } from '@/shared/lib/platform';
import { env } from '@/shared/config';

type ErrorKind = 'render' | 'error' | 'rejection' | 'chunk' | 'request';

interface Report {
  kind: ErrorKind;
  message: string;
  stack?: string;
  componentStack?: string;
  page: string;
  build?: string;
  platform?: string;
}

/** Одна и та же ошибка за сессию — один раз: цикл ре-рендеров не должен заспамить чат. */
const sent = new Set<string>();

/**
 * Какая сборка у человека — имя стартового чанка с хешем. После деплоя
 * старая вкладка просит чанки, которых на сервере уже нет; по этому имени
 * видно, что упала именно старая сборка.
 */
function currentBuild(): string | undefined {
  const src = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute('src');
  return src?.split('/').pop();
}

/** Клиент Telegram и его версия: «ios 8.4» — сбой 2026-10-11 был только на телефоне. Вне Telegram — `web`. */
function currentPlatform(): string | undefined {
  if (!isTelegram()) return 'web';
  try {
    const params = retrieveLaunchParams();
    return `${params.tgWebAppPlatform} ${params.tgWebAppVersion}`;
  } catch {
    return undefined;
  }
}

/**
 * Ошибка фронта — на бэкенд (`POST /client-errors`), оттуда в лог сервера и
 * служебный чат. Консоли телефона в Telegram не видно, так что без этого
 * упавший экран не оставлял следа нигде. Только путь страницы, без `hash` и
 * `search`: в hash Telegram кладёт данные запуска.
 */
export function reportError(kind: ErrorKind, error: unknown, componentStack?: string): void {
  if (env.isDev) return;
  // Не `Error` (строка, «Script error.») — своего стека нет. `new Error` здесь
  // дал бы стек самого `reportError`, и он выглядел бы как место падения.
  const isError = error instanceof Error;
  const message = isError ? error.message : String(error);
  const key = `${kind}:${message}`;
  if (sent.has(key)) return;
  sent.add(key);
  send({
    kind,
    message,
    stack: isError ? error.stack : undefined,
    componentStack,
    page: window.location.pathname,
    build: currentBuild(),
    platform: currentPlatform(),
  });
}

/**
 * Ответы, которые приложение ждёт и показывает как состояние, а не как сбой:
 * ход ещё идёт и «подписка уже активна» (409), в записи не расслышали ни
 * слова (422), технические работы (503 `maintenance`), повторная оценка
 * уровня закрыта до Премиума (403 `assessment_locked`), гость веб-версии на закрытом маршруте
 * (403 `login_required`). Всё остальное —
 * в чат, включая 401 и 404.
 */
function expected(error: HttpError | NetworkError | TimeoutError): boolean {
  if (!(error instanceof HttpError)) return false;
  if (error.status === 409 || error.status === 422) return true;
  if (isMaintenanceError(error)) return true;
  // Гость веб-версии дошёл до закрытого — его ведут на вход, это не сбой.
  if (isLoginRequiredError(error)) return true;
  return error.status === 403 && error.body.includes('assessment_locked');
}

/** Путь запроса без хоста и query: в query бывают тексты пользователя. */
function requestPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

/** Сколько часов назад выдан initData, с которым ушёл запрос, — ради 401 `expired`. */
function initDataAge(): string | undefined {
  const raw = authHeaders().Authorization?.slice('tma '.length);
  const authDate = raw ? Number(new URLSearchParams(raw).get('auth_date')) : 0;
  return authDate ? `${((Date.now() / 1000 - authDate) / 3600).toFixed(1)} ч` : undefined;
}

/**
 * Неудачный запрос — всё, что человек видит экраном ошибки, тостом или
 * кнопкой, которая ничего не сделала. Подключается в `main.tsx` через
 * `setRequestFailureListener`, поэтому ловит и `apiRequest`, и свои `fetch`
 * голоса и диалога. Отчёт о самом отчёте не шлём — иначе петля.
 */
export function reportRequestFailure(error: HttpError | NetworkError | TimeoutError): void {
  if (expected(error)) return;
  const path = requestPath(error.url);
  if (path.endsWith('/client-errors')) return;
  const what =
    error instanceof HttpError
      ? `${error.status} ${path}${error.body ? ` — ${error.body.slice(0, 200)}` : ''}`
      : `${error instanceof TimeoutError ? 'таймаут' : 'нет сети'} ${path}`;
  const age = error instanceof HttpError && error.status === 401 ? initDataAge() : undefined;
  reportError('request', new Error(age ? `${what} (initData ${age})` : what));
}

/**
 * Отчёты, которые не ушли (нет сети — частая причина самой ошибки), ждут в
 * `localStorage` и уходят при следующем запуске или когда сеть вернётся.
 */
const QUEUE_KEY = 'client-errors:queue';
const QUEUE_MAX = 20;

function readQueue(): Report[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as Report[];
  } catch {
    return [];
  }
}

function writeQueue(queue: Report[]): void {
  try {
    if (queue.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-QUEUE_MAX)));
    else localStorage.removeItem(QUEUE_KEY);
  } catch {
    // Нет хранилища — отчёт просто потеряется, как раньше.
  }
}

function send(report: Report): void {
  apiRequest('/client-errors', { method: 'POST', keepalive: true, body: report }).catch(() => {
    writeQueue([...readQueue(), report]);
  });
}

/** Зовётся из `main.tsx`, когда initData уже подставлен в запросы, — иначе отчёт ушёл бы без «кто». */
export function flushQueue(): void {
  const queue = readQueue();
  if (!queue.length) return;
  writeQueue([]);
  queue.forEach(send);
}

/** Исключения мимо React (обработчики, таймеры) и промисы без `catch`. */
export function reportGlobalErrors(): void {
  window.addEventListener('error', (event) => {
    if (event.error != null) {
      reportError('error', event.error);
      return;
    }
    // Ошибка из кода не с нашего origin: браузер прячет и текст, и место —
    // остаётся «Script error.» без файла. Наших чужих скриптов в мини-аппе
    // нет (Метрика только в вебе), так что это код, который Telegram Android
    // подставляет в WebView, или расширение браузера. Чинить нечего, в чат не шлём.
    if (event.message === 'Script error.' && !event.filename) return;
    const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : '';
    reportError('error', `${event.message}${where}`);
  });
  window.addEventListener('unhandledrejection', (event) => {
    // Неудачный запрос уже ушёл отчётом `request` — второй под `rejection` не нужен.
    const reason: unknown = event.reason;
    if (reason instanceof HttpError || reason instanceof NetworkError || reason instanceof TimeoutError) return;
    reportError('rejection', reason);
  });
  if (!env.isDev) window.addEventListener('online', flushQueue);
}
